import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import VideoParticipant from './VideoParticipant';
import { selectAdmittedParticipants } from '../../selectors/mediaSelectors';
import {
  setScreenShareLayout,
  toggleScreenSharing,
} from '../../store/slices/mediaSlice';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';
import { getStoredMeetingSessionId } from '../../utils/meetingAuth';
import { resolveRemoteMediaIntent } from '../../utils/mediaReliability';

const getVideoTrack = (source) => {
  if (!source) return null;
  if (typeof source.getVideoTracks === 'function') {
    return source.getVideoTracks()[0] || null;
  }
  return source.kind === 'video' ? source : null;
};

const isLiveTrack = (track) => Boolean(track && track.readyState !== 'ended');

const getLocalScreenEventSource = (event) => {
  const detail = event?.detail;
  if (!detail) return null;
  if (
    typeof detail.getVideoTracks === 'function' ||
    detail.kind === 'video'
  ) {
    return detail;
  }
  return (
    detail.stream ||
    detail.screenStream ||
    detail.source ||
    detail.track ||
    null
  );
};

/**
 * The presentation video is deliberately kept separate from participant
 * camera tiles.  It has one permanent DOM node and only swaps srcObject when
 * the actual MediaStream changes, preventing a full-stage flash when duplicate
 * Socket.IO producer announcements arrive.
 */
const PresentationVideo = React.memo(({ stream }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return undefined;

    if (stream && element.srcObject !== stream) {
      element.srcObject = stream;
    }

    if (stream) element.play?.().catch(() => undefined);

    return () => {
      if (element.srcObject === stream) element.srcObject = null;
    };
  }, [stream]);

  const track = getVideoTrack(stream);
  if (!isLiveTrack(track)) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#202124] text-sm text-gray-300">
        Connecting shared screen…
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="h-full w-full bg-[#202124] object-contain"
    />
  );
});

/**
 * Rendering the presenter's captured display back into the same meeting tab
 * creates the familiar infinite hall-of-mirrors effect. The capture stream is
 * still owned and published by the media layer; only this local stage preview
 * is replaced. Remote participants continue to render PresentationVideo.
 */
const LocalScreenSharePlaceholder = () => (
  <div
    className="flex h-full w-full flex-col items-center justify-center bg-[#202124] px-6 text-center text-white"
    data-local-screen-share-placeholder="true"
    role="status"
    aria-label="You are sharing your screen"
  >
    <div className="relative mb-5 h-20 w-28 rounded-xl border-2 border-emerald-300/80 bg-[#303134] shadow-[0_14px_45px_rgba(16,185,129,0.2)]" aria-hidden="true">
      <div className="absolute inset-3 rounded-md border border-white/30 bg-[#202124]" />
      <span className="absolute -bottom-3 left-1/2 h-3 w-8 -translate-x-1/2 rounded-b bg-emerald-300/80" />
    </div>
    <h2 className="text-lg font-semibold sm:text-xl">You’re sharing your screen</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-gray-300">
      Your shared content is hidden in this meeting window to prevent an infinite mirror. Participants continue to receive it normally.
    </p>
  </div>
);

const PresentationLayoutControl = ({ layout, onChange }) => (
  <div
    role="group"
    aria-label="Your screen share layout"
    className="pointer-events-auto flex items-center rounded-full border border-white/20 bg-black/70 p-0.5 text-[11px] font-semibold text-white shadow-lg backdrop-blur"
  >
    {[
      ['split', 'Split', 'Show your shared content with the participant rail'],
      ['focus', 'Focus', 'Show only your shared content'],
    ].map(([value, label, description]) => (
      <button
        key={value}
        type="button"
        aria-pressed={layout === value}
        aria-label={`${label} screen share layout`}
        title={description}
        onClick={() => onChange(value)}
        className={`rounded-full px-2.5 py-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
          layout === value
            ? 'bg-white text-gray-900'
            : 'text-white hover:bg-white/15'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const PresentationHeader = ({
  presenterName,
  isLocal,
  layout,
  onLayoutChange,
  onStop,
}) => (
  <div className="pointer-events-none absolute left-3 right-3 top-3 z-30 flex items-start justify-between gap-3">
    <div className="max-w-[75%] rounded-full bg-black/75 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur sm:text-sm">
      {isLocal ? 'You are presenting' : `${presenterName} is presenting`}
    </div>
    <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-2">
      {isLocal && onLayoutChange && (
        <PresentationLayoutControl
          layout={layout}
          onChange={onLayoutChange}
        />
      )}
      {isLocal && onStop && (
        <button
          type="button"
          onClick={onStop}
          className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:px-4 sm:py-2 sm:text-sm"
        >
          Stop sharing
        </button>
      )}
    </div>
  </div>
);

const VideoGrid = React.memo(
  ({
    remoteStreams,
    remoteVideoStatus,
    remoteScreens = {},
    mainVideoRef,
    onStopLocalShare,
    onRetryRemoteVideo,
    onRetryVideoDelivery,
    onRetryAudioDelivery,
    onRemoteVideoHealthChange,
    videoDeliveryReports = {},
    audioDeliveryReports = {},
    viewMode = 'sidebar',
  }) => {
    const dispatch = useDispatch();
    const localStream = useSelector((state) => state.media.localStream);
    const videoEnabled = useSelector((state) => state.media.videoEnabled);
    const audioEnabled = useSelector((state) => state.media.audioEnabled);
    const screenSharing = useSelector((state) => state.media.screenSharing);
    const screenShareLayout = useSelector(
      (state) => state.media.screenShareLayout || 'split'
    );
    const authUser = useSelector((state) => state.auth.user);
    const participants = useSelector(selectAdmittedParticipants) || [];
    const participantNetworkStats = useSelector(
      (state) =>
        state?.room?.participantNetworkStats ||
        state?.room?.currentRoom?.participantNetworkStats ||
        {}
    );

    const mySessionId = String(getStoredMeetingSessionId() || 'local');
    const meetingStatus = sessionStorage.getItem('statusget');
    const [spotlightSessionId, setSpotlightSessionId] = useState(null);
    const [autoSpotlight, setAutoSpotlight] = useState(true);
    const [reactiveLocalScreenSource, setReactiveLocalScreenSource] =
      useState(null);
    const lastSpotlightChangeRef = useRef(0);
    const streamCacheRef = useRef({
      audio: new Map(),
      video: new Map(),
      screen: new Map(),
    });

    const getStableStream = useCallback((kind, sessionId, source) => {
      const track = kind === 'screen' ? getVideoTrack(source) : source;
      if (!track || track.readyState === 'ended') return null;

      const key = String(sessionId || track.id);
      const cache = streamCacheRef.current[kind];
      let stream = cache.get(key);
      if (!stream) {
        stream = new MediaStream();
        cache.set(key, stream);
      }

      for (const existing of stream.getTracks()) {
        if (existing.kind === track.kind && existing.id !== track.id) {
          stream.removeTrack(existing);
        }
      }
      if (!stream.getTracks().some((existing) => existing.id === track.id)) {
        stream.addTrack(track);
      }
      return stream;
    }, []);

    const filteredStreams = useMemo(() => {
      const unique = {};
      for (const [sessionId, streamData] of Object.entries(remoteStreams || {})) {
        if (!unique[sessionId]) unique[sessionId] = streamData || {};
      }
      return unique;
    }, [remoteStreams]);

    const speakersRef = useRef(new Map());
    const [, setSpeakerVersion] = useState(0);
    const speakerFrameRef = useRef(null);

    const handleSpeakingChange = useCallback(({ sessionId, speaking, level }) => {
      const key = String(sessionId || '');
      if (!key) return;
      const previous = speakersRef.current.get(key);
      const changed =
        !previous ||
        previous.speaking !== speaking ||
        Math.abs(Number(previous.level || 0) - Number(level || 0)) > 0.08;
      if (!changed) return;

      speakersRef.current.set(key, {
        speaking: Boolean(speaking),
        level: Number(level || 0),
        updatedAt: Date.now(),
      });

      if (!speakerFrameRef.current) {
        speakerFrameRef.current = requestAnimationFrame(() => {
          setSpeakerVersion((value) => value + 1);
          speakerFrameRef.current = null;
        });
      }
    }, []);

    useEffect(
      () => () => {
        if (speakerFrameRef.current) cancelAnimationFrame(speakerFrameRef.current);
      },
      []
    );

    const orderedParticipants = useMemo(() => {
      const now = Date.now();
      const score = (sessionId) => {
        const speaker = speakersRef.current.get(String(sessionId));
        if (!speaker) return 0;
        const recent = Math.max(0, 1 - (now - speaker.updatedAt) / 5000);
        return (speaker.speaking ? 3 : 0) + recent + speaker.level;
      };

      return participants
        .filter((participant) => String(participant.sessionId) !== mySessionId)
        .slice()
        .sort(
          (left, right) =>
            score(right.sessionId) - score(left.sessionId)
        );
    }, [participants, mySessionId]);

    const deliveryReceiverSessionIds = useMemo(
      () =>
        participants
          .filter(
            (participant) =>
              String(participant?.sessionId || '') !== mySessionId &&
              participant?.disconnected !== true
          )
          .map((participant) => String(participant.sessionId || '').trim())
          .filter(Boolean),
      [participants, mySessionId]
    );

    const participantBySession = useMemo(
      () =>
        new Map(
          participants.map((participant) => [
            String(participant.sessionId || ''),
            participant,
          ])
        ),
      [participants]
    );

    useEffect(() => {
      const syncLocalScreenStream = (event) => {
        const source = event
          ? getLocalScreenEventSource(event)
          : window.mediaSoupHandler?.screenStream || null;
        setReactiveLocalScreenSource(
          isLiveTrack(getVideoTrack(source)) ? source : null
        );
      };

      syncLocalScreenStream();
      window.addEventListener(
        'meeting-local-screen-stream-change',
        syncLocalScreenStream
      );
      return () =>
        window.removeEventListener(
          'meeting-local-screen-stream-change',
          syncLocalScreenStream
        );
    }, []);

    const localScreenStream = useMemo(() => {
      if (!screenSharing) return null;
      const reactiveTrack = getVideoTrack(reactiveLocalScreenSource);
      if (isLiveTrack(reactiveTrack)) {
        return typeof reactiveLocalScreenSource?.getVideoTracks === 'function'
          ? reactiveLocalScreenSource
          : getStableStream('screen', mySessionId, reactiveTrack);
      }

      const handlerStream = window.mediaSoupHandler?.screenStream;
      const handlerTrack = getVideoTrack(handlerStream);
      if (isLiveTrack(handlerTrack)) return handlerStream;

      const echoedTrack = getVideoTrack(remoteScreens?.[mySessionId]);
      return isLiveTrack(echoedTrack)
        ? getStableStream('screen', mySessionId, echoedTrack)
        : null;
    }, [
      getStableStream,
      mySessionId,
      reactiveLocalScreenSource,
      remoteScreens,
      screenSharing,
    ]);

    const remotePresentations = useMemo(
      () =>
        Object.entries(remoteScreens || {})
          .map(([sessionId, source]) => [String(sessionId), getVideoTrack(source)])
          .filter(
            ([sessionId, track]) =>
              sessionId !== mySessionId && isLiveTrack(track)
          ),
      [mySessionId, remoteScreens]
    );

    // Pin the current remote presenter while its track remains live. Object
    // insertion order or duplicate socket updates must not change the stage.
    const [remotePresenterSessionId, setRemotePresenterSessionId] = useState(null);
    useEffect(() => {
      setRemotePresenterSessionId((current) => {
        if (
          current &&
          remotePresentations.some(([sessionId]) => sessionId === current)
        ) {
          return current;
        }
        return remotePresentations[0]?.[0] || null;
      });
    }, [remotePresentations]);

    const presentationSessionId = localScreenStream
      ? mySessionId
      : remotePresenterSessionId;
    const presentationIsLocal = presentationSessionId === mySessionId;
    const presentationTrack = presentationIsLocal
      ? getVideoTrack(localScreenStream)
      : remotePresentations.find(
          ([sessionId]) => sessionId === presentationSessionId
        )?.[1] || null;
    const presentationStream = presentationIsLocal
      ? localScreenStream
      : presentationTrack
        ? getStableStream('screen', presentationSessionId, presentationTrack)
        : null;
    const presenter = participantBySession.get(String(presentationSessionId || ''));
    const presenterName = presentationIsLocal
      ? getMeetingDisplayName(authUser, 'You')
      : getMeetingDisplayName(presenter, 'Shared screen');
    const hasPresentation = Boolean(
      presentationSessionId && isLiveTrack(getVideoTrack(presentationStream))
    );

    useEffect(() => {
      if (!screenSharing) return undefined;
      const track = getVideoTrack(localScreenStream);
      if (!track) return undefined;
      const handleEnded = () => dispatch(toggleScreenSharing(false));
      track.addEventListener('ended', handleEnded);
      return () => track.removeEventListener('ended', handleEnded);
    }, [dispatch, localScreenStream, screenSharing]);

    const stopLocalPresentation = useCallback(async () => {
      try {
        if (onStopLocalShare) {
          await onStopLocalShare();
        } else {
          await window.mediaSoupHandler?.stopScreenSharing?.();
        }
        dispatch(toggleScreenSharing(false));
        toast.success('Screen sharing stopped');
      } catch (error) {
        toast.error(`Unable to stop screen sharing: ${error.message}`);
      }
    }, [dispatch, onStopLocalShare]);

    const changeScreenShareLayout = useCallback(
      (layout) => {
        dispatch(setScreenShareLayout(layout));
      },
      [dispatch]
    );

    // Active-speaker spotlight is used only when nobody is presenting.
    useEffect(() => {
      if (!autoSpotlight || hasPresentation) return undefined;
      const timer = window.setInterval(() => {
        const now = Date.now();
        const candidates = [...speakersRef.current.entries()]
          .filter(
            ([, speaker]) =>
              speaker.speaking &&
              speaker.level > 0.1 &&
              now - speaker.updatedAt < 1200
          )
          .sort((left, right) => right[1].level - left[1].level);
        if (!candidates.length || now - lastSpotlightChangeRef.current < 900) {
          return;
        }

        const next = candidates[0][0];
        setSpotlightSessionId((current) => {
          const resolved = next === mySessionId ? null : next;
          if (current === resolved) return current;
          lastSpotlightChangeRef.current = now;
          return resolved;
        });
      }, 500);
      return () => window.clearInterval(timer);
    }, [autoSpotlight, hasPresentation, mySessionId]);

    useEffect(() => {
      if (!spotlightSessionId) return;
      if (!participantBySession.has(String(spotlightSessionId))) {
        setSpotlightSessionId(null);
      }
    }, [participantBySession, spotlightSessionId]);

    if (!authUser) {
      return (
        <div className="flex h-full items-center justify-center bg-gray-900 text-white">
          Loading participants…
        </div>
      );
    }
    if (meetingStatus === 'waiting') return null;

    const participantIdentity = (participant) => ({
      userId: String(
        participant?.userId ||
          participant?.user?._id ||
          participant?.user?.id ||
          participant?.user ||
          participant?.sessionId ||
          'participant'
      ),
      username: getMeetingDisplayName(participant),
    });

    const renderLocalCamera = (wrapperClass, key = 'local-camera') => (
      <div key={key} className={wrapperClass}>
        <VideoParticipant
          userId={String(authUser._id || authUser.id || 'local')}
          username={getMeetingDisplayName(authUser, 'You')}
          stream={localStream}
          audioStream={localStream}
          screenStream={null}
          isLocal
          isVideoEnabled={videoEnabled}
          isVideoStateKnown
          isAudioEnabled={audioEnabled}
          isAudioStateKnown
          participant={{ sessionId: mySessionId }}
          networkStats={participantNetworkStats?.[mySessionId]}
          sessionId={mySessionId}
          mainVideoRef={mainVideoRef}
          onSpeakingChange={handleSpeakingChange}
          onRetryDelivery={onRetryVideoDelivery}
          onRetryAudioDelivery={onRetryAudioDelivery}
          deliveryReports={videoDeliveryReports}
          audioDeliveryReports={audioDeliveryReports}
          deliveryReceiverSessionIds={deliveryReceiverSessionIds}
        />
      </div>
    );

    const renderRemoteCamera = (participant, wrapperClass, keyPrefix = 'camera') => {
      const sessionId = String(participant.sessionId || '');
      const streams = filteredStreams[sessionId] || {};
      const identity = participantIdentity(participant);
      const videoTrack = streams.video;
      const audioTrack = streams.audio;
      const hasSocketVideoState = Object.prototype.hasOwnProperty.call(
        remoteVideoStatus || {},
        sessionId
      );
      const videoIntent = resolveRemoteMediaIntent({
        socketValue: hasSocketVideoState
          ? remoteVideoStatus[sessionId]
          : undefined,
        participantValue: participant.videoEnabled,
        hasLiveTrack: isLiveTrack(videoTrack),
      });
      const audioIntent = resolveRemoteMediaIntent({
        participantValue: participant.audioEnabled,
        hasLiveTrack: isLiveTrack(audioTrack),
      });
      return (
        <div key={`${keyPrefix}-${sessionId}`} className={wrapperClass}>
          <VideoParticipant
            userId={identity.userId}
            username={identity.username}
            stream={getStableStream('video', sessionId, videoTrack)}
            audioStream={getStableStream('audio', sessionId, audioTrack)}
            screenStream={null}
            isLocal={false}
            isVideoEnabled={videoIntent.enabled}
            isVideoStateKnown={videoIntent.known}
            isAudioEnabled={audioIntent.enabled}
            isAudioStateKnown={audioIntent.known}
            participant={participant}
            networkStats={participantNetworkStats?.[sessionId]}
            sessionId={sessionId}
            onSpeakingChange={handleSpeakingChange}
            onRetryVideo={onRetryRemoteVideo}
            onRemoteVideoHealthChange={onRemoteVideoHealthChange}
          />
        </div>
      );
    };

    if (hasPresentation) {
      const showPresentationParticipantRail =
        !presentationIsLocal || screenShareLayout !== 'focus';
      return (
        <div
          className={
            showPresentationParticipantRail
              ? 'grid h-full w-full min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-2 bg-[#202124] p-2 lg:grid-cols-[minmax(0,1fr)_280px] lg:grid-rows-1 lg:gap-3 lg:p-3'
              : 'h-full w-full min-h-0 bg-[#202124] p-2 lg:p-3'
          }
          data-screen-share-layout={
            presentationIsLocal ? screenShareLayout : 'split'
          }
        >
          <section className="relative h-full min-h-0 overflow-hidden rounded-2xl border border-white/15 bg-[#202124] shadow-2xl">
            <PresentationHeader
              presenterName={presenterName}
              isLocal={presentationIsLocal}
              layout={screenShareLayout}
              onLayoutChange={
                presentationIsLocal ? changeScreenShareLayout : null
              }
              onStop={presentationIsLocal ? stopLocalPresentation : null}
            />
            {presentationIsLocal ? (
              <LocalScreenSharePlaceholder />
            ) : (
              <PresentationVideo stream={presentationStream} />
            )}
          </section>

          <aside
            className={
              showPresentationParticipantRail
                ? 'meeting-participant-scroll flex min-h-[116px] gap-2 overflow-x-auto overflow-y-hidden pb-1 lg:h-full lg:min-h-0 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:overscroll-contain lg:pr-1'
                : 'pointer-events-none fixed -left-[10000px] top-0 h-px w-px overflow-hidden opacity-0'
            }
            aria-hidden={!showPresentationParticipantRail}
            data-audio-mounted={
              showPresentationParticipantRail
                ? 'visible-rail'
                : 'focus-audio-only'
            }
            data-testid="presentation-participant-scroll"
          >
            {renderLocalCamera(
              'h-28 w-44 shrink-0 overflow-hidden rounded-2xl sm:h-32 sm:w-52 lg:h-40 lg:w-full',
              'presentation-local-camera'
            )}
            {orderedParticipants.map((participant) =>
              renderRemoteCamera(
                participant,
                'h-28 w-44 shrink-0 overflow-hidden rounded-2xl sm:h-32 sm:w-52 lg:h-40 lg:w-full',
                'presentation-camera'
              )
            )}
          </aside>
        </div>
      );
    }

    const allCameraParticipants = [
      { sessionId: mySessionId, local: true },
      ...orderedParticipants.map((participant) => ({ ...participant, local: false })),
    ];

    if (viewMode === 'tiles') {
      const count = allCameraParticipants.length;
      const columns =
        count <= 1
          ? 'grid-cols-1'
          : count <= 4
            ? 'grid-cols-1 sm:grid-cols-2'
            : count <= 9
              ? 'grid-cols-2 lg:grid-cols-3'
              : 'grid-cols-2 lg:grid-cols-4';
      return (
        <div className={`grid h-full min-h-0 w-full content-start auto-rows-[minmax(170px,1fr)] gap-2 overflow-y-auto overscroll-contain touch-pan-y bg-[#202124] p-2 pb-6 sm:gap-3 sm:p-4 ${columns}`}>
          {allCameraParticipants.map((participant) =>
            participant.local
              ? renderLocalCamera(
                  'min-h-[170px] overflow-hidden rounded-2xl',
                  'tile-local'
                )
              : renderRemoteCamera(
                  participant,
                  'min-h-[170px] overflow-hidden rounded-2xl',
                  'tile'
                )
          )}
        </div>
      );
    }

    const mainParticipant = spotlightSessionId
      ? participantBySession.get(String(spotlightSessionId))
      : null;
    const mainIsLocal = !mainParticipant;
    const mainSessionId = mainIsLocal ? mySessionId : String(mainParticipant.sessionId);
    const sideParticipants = mainIsLocal
      ? orderedParticipants
      : orderedParticipants.filter(
          (participant) => String(participant.sessionId) !== mainSessionId
        );

    return (
      <div className="grid h-full w-full min-h-0 grid-rows-[minmax(0,1fr)_auto] gap-2 bg-[#202124] p-2 lg:grid-cols-[minmax(0,1fr)_280px] lg:grid-rows-1 lg:gap-3 lg:p-3">
        <div
          role="button"
          tabIndex={spotlightSessionId ? 0 : -1}
          className="min-h-0 overflow-hidden rounded-2xl bg-black text-left"
          onClick={() => {
            if (!spotlightSessionId) return;
            setAutoSpotlight(true);
            setSpotlightSessionId(null);
          }}
          onKeyDown={(event) => {
            if (!spotlightSessionId || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            setAutoSpotlight(true);
            setSpotlightSessionId(null);
          }}
          title={spotlightSessionId ? 'Return to your camera' : undefined}
        >
          {mainIsLocal
            ? renderLocalCamera('h-full w-full', 'main-local')
            : renderRemoteCamera(mainParticipant, 'h-full w-full', 'main-remote')}
        </div>

        <aside
          className="meeting-participant-scroll flex min-h-[116px] gap-2 overflow-x-auto overflow-y-hidden pb-1 lg:h-full lg:min-h-0 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:overscroll-contain lg:pr-1"
          data-testid="sidebar-participant-scroll"
        >
          {!mainIsLocal &&
            renderLocalCamera(
              'h-28 w-44 shrink-0 overflow-hidden rounded-2xl sm:h-32 sm:w-52 lg:h-40 lg:w-full',
              'sidebar-local'
            )}
          {sideParticipants.map((participant) => (
            <div
              role="button"
              tabIndex={0}
              key={`sidebar-button-${participant.sessionId}`}
              className="h-28 w-44 shrink-0 overflow-hidden rounded-2xl text-left sm:h-32 sm:w-52 lg:h-40 lg:w-full"
              onClick={() => {
                setAutoSpotlight(false);
                setSpotlightSessionId(String(participant.sessionId));
              }}
              onKeyDown={(event) => {
                if (!['Enter', ' '].includes(event.key)) return;
                event.preventDefault();
                setAutoSpotlight(false);
                setSpotlightSessionId(String(participant.sessionId));
              }}
              title="Spotlight participant"
            >
              {renderRemoteCamera(participant, 'h-full w-full', 'sidebar')}
            </div>
          ))}
        </aside>
      </div>
    );
  },
  (previous, next) =>
    previous.remoteStreams === next.remoteStreams &&
    previous.remoteVideoStatus === next.remoteVideoStatus &&
    previous.remoteScreens === next.remoteScreens &&
    previous.mainVideoRef === next.mainVideoRef &&
    previous.onStopLocalShare === next.onStopLocalShare &&
    previous.onRetryRemoteVideo === next.onRetryRemoteVideo &&
    previous.onRetryVideoDelivery === next.onRetryVideoDelivery &&
    previous.onRetryAudioDelivery === next.onRetryAudioDelivery &&
    previous.onRemoteVideoHealthChange === next.onRemoteVideoHealthChange &&
    previous.videoDeliveryReports === next.videoDeliveryReports &&
    previous.audioDeliveryReports === next.audioDeliveryReports &&
    previous.viewMode === next.viewMode
);

export default VideoGrid;
